import { buildApiUrl, getAuthHeaders } from '../config/api';

export interface AIPersona {
  id: string;
  name: string;
  type: string;
  description: string;
  isActive: boolean;
}



export interface AIReviewResult {
  persona: {
    id: string;
    name: string;
    type: string;
  };
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'REQUIRES_ATTENTION';
  progress: number;
  analysis: {
    summary: string;
    confidence_score: number;
    risks: Array<{
      id: string;
      title: string;
      description: string;
      severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
      category: string;
      recommendation: string;
      source_documents: string[];
    }>;
    recommendations: Array<{
      id: string;
      title: string;
      description: string;
      priority: 'LOW' | 'MEDIUM' | 'HIGH';
      action_required: boolean;
      source_documents: string[];
    }>;
    extracted_data: {
      [chapterName: string]: {
        [categoryName: string]: Array<{
          question: string;
          answer: string;
          confidence: number;
          source_document: string;
          page_reference?: string;
          verification_status: 'VERIFIED' | 'FLAGGED' | 'PENDING';
        }>;
      };
    };
    document_insights: {
      [documentId: string]: {
        processed_at: string;
        key_findings: string[];
        risk_level: 'LOW' | 'MEDIUM' | 'HIGH';
        completeness_score: number;
      };
    };
  };
  documentsAnalyzed: number;
  totalDocuments: number;
  currentDocument?: string;
  errorMessage?: string;
  estimatedTimeRemaining: number;
  startedAt?: string;
  completedAt?: string;
  lastUpdated: string;
}



class AIReviewService {
  // Helper method for API calls
  private async apiCall(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = buildApiUrl(endpoint);
    const headers = getAuthHeaders();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle authentication errors gracefully
      if (response.status === 401) {
        console.warn('Authentication required for endpoint:', endpoint);
        throw new Error('Authentication required. Please log in to access this feature.');
      }

      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Start AI review for a deal
  async startReview(dealId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.apiCall('/ai-review/start', {
        method: 'POST',
        body: JSON.stringify({ dealId }),
      });
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to start AI review');
    }
  }

  // Get AI review results with progress and status
  async getResults(dealId: string, filters?: {
    answerFilter?: 'all' | 'answered' | 'not_answered';
    confidenceFilter?: 'all' | 'high' | 'medium' | 'low';
    sourceFilter?: string;
    personaType?: string;
  }): Promise<{
    results: AIReviewResult[];
    summary: {
      dealId: string;
      isRunning: boolean;
      overallProgress: number;
      totalDocuments: number;
      totalProcessedDocuments: number;
      totalPersonas: number;
      completedPersonas: number;
      failedPersonas: number;
      estimatedCompletionTime: Date | null;
    };
  }> {
    try {
      let url = `/ai-review/results/${dealId}`;
      
      // Add query parameters if filters are provided
      if (filters) {
        const params = new URLSearchParams();
        if (filters.answerFilter) params.append('answerFilter', filters.answerFilter);
        if (filters.confidenceFilter) params.append('confidenceFilter', filters.confidenceFilter);
        if (filters.sourceFilter) params.append('sourceFilter', filters.sourceFilter);
        if (filters.personaType) params.append('personaType', filters.personaType);
        
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }
      
      const response = await this.apiCall(url);
      return {
        results: response.data,
        summary: response.summary
      };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get AI review results');
    }
  }

  // Get available AI personas
  async getPersonas(): Promise<AIPersona[]> {
    try {
      const response = await this.apiCall('/ai-review/personas');
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get AI personas');
    }
  }

  // Test Gemini API connection
  async testConnection(): Promise<{ connected: boolean; message: string }> {
    try {
      const response = await this.apiCall('/ai-review/test-connection', {
        method: 'POST',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to test connection');
    }
  }

  // Initialize AI personas
  async initializePersonas(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.apiCall('/ai-review/initialize-personas', {
        method: 'POST',
      });
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to initialize personas');
    }
  }

  // Reset stuck AI review
  async resetReview(dealId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.apiCall(`/ai-review/reset/${dealId}`, {
        method: 'POST',
      });
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to reset AI review');
    }
  }

  // Sync AI Review records with the latest list of selected personas for a deal
  async syncPersonas(dealId: string, personaTypes: string[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.apiCall('/ai-review/sync-personas', {
        method: 'POST',
        body: JSON.stringify({ dealId, personaTypes }),
      });
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to sync AI review personas');
    }
  }

  // Helper method to get persona icon based on type
  getPersonaIcon(type: string): string {
    const iconMap: { [key: string]: string } = {
      'CORPORATE_SECRETARIAL': 'Building',
      'FINANCIAL_INDEBTEDNESS': 'DollarSign',
      'PROJECTS': 'Cog',
      'MATERIAL_CONTRACTS': 'FileText',
      'ASSETS': 'Home',
      'MATERIAL_APPROVALS_LICENSES': 'Shield',
      'HUMAN_RESOURCE': 'Users',
      'INTELLECTUAL_PROPERTY_RIGHTS': 'Lightbulb',
      'INSURANCES': 'Umbrella',
      'LITIGATIONS_DISPUTES': 'Scale'
    };
    return iconMap[type] || 'Bot';
  }

  // Helper method to get persona color based on type
  getPersonaColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      'CORPORATE_SECRETARIAL': 'text-blue-600',
      'FINANCIAL_INDEBTEDNESS': 'text-green-600',
      'PROJECTS': 'text-purple-600',
      'MATERIAL_CONTRACTS': 'text-orange-600',
      'ASSETS': 'text-cyan-600',
      'MATERIAL_APPROVALS_LICENSES': 'text-red-600',
      'HUMAN_RESOURCE': 'text-pink-600',
      'INTELLECTUAL_PROPERTY_RIGHTS': 'text-violet-600',
      'INSURANCES': 'text-emerald-600',
      'LITIGATIONS_DISPUTES': 'text-slate-600'
    };
    return colorMap[type] || 'text-gray-600';
  }

  // Helper method to format status for display
  formatStatus(status: string): string {
    return status.toLowerCase().replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  // Helper method to calculate overall confidence
  calculateOverallConfidence(results: AIReviewResult[]): number {
    if (results.length === 0) return 0;

    const completedResults = results.filter(r => r.status === 'COMPLETED' && r.analysis);
    if (completedResults.length === 0) return 0;

    const totalConfidence = completedResults.reduce((sum, result) =>
      sum + (result.analysis?.confidence_score || 0), 0
    );

    return Math.round(totalConfidence / completedResults.length);
  }

  // Helper method to get all risks from results
  getAllRisks(results: AIReviewResult[]): Array<any> {
    const allRisks: Array<any> = [];

    results.forEach(result => {
      if (result.analysis?.risks) {
        result.analysis.risks.forEach(risk => {
          allRisks.push({
            ...risk,
            persona: result.persona.name,
            personaType: result.persona.type
          });
        });
      }
    });

    // Sort by severity
    const severityOrder = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
    return allRisks.sort((a, b) =>
      (severityOrder[b.severity as keyof typeof severityOrder] || 0) -
      (severityOrder[a.severity as keyof typeof severityOrder] || 0)
    );
  }

  // Helper method to get all recommendations from results
  getAllRecommendations(results: AIReviewResult[]): Array<any> {
    const allRecommendations: Array<any> = [];

    results.forEach(result => {
      if (result.analysis?.recommendations) {
        result.analysis.recommendations.forEach(rec => {
          allRecommendations.push({
            ...rec,
            persona: result.persona.name,
            personaType: result.persona.type
          });
        });
      }
    });

    // Sort by priority
    const priorityOrder = { 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
    return allRecommendations.sort((a, b) =>
      (priorityOrder[b.priority as keyof typeof priorityOrder] || 0) -
      (priorityOrder[a.priority as keyof typeof priorityOrder] || 0)
    );
  }

  // Helper method to get all extracted data from results
  getAllExtractedData(results: AIReviewResult[]): Array<any> {
    const allData: Array<any> = [];

    results.forEach(result => {
      if (result.analysis?.extracted_data) {
        // Handle new chapter-based structure
        Object.keys(result.analysis.extracted_data).forEach(chapterName => {
          Object.keys(result.analysis.extracted_data[chapterName]).forEach(categoryName => {
            result.analysis.extracted_data[chapterName][categoryName].forEach(qa => {
              allData.push({
                field: qa.question,
                value: qa.answer,
                confidence: qa.confidence,
                source_document: qa.source_document,
                verification_status: qa.verification_status,
                page_reference: qa.page_reference,
                persona: result.persona.name,
                personaType: result.persona.type,
                chapter: chapterName,
                category: categoryName
              });
            });
          });
        });
      }
    });

    return allData.sort((a, b) => b.confidence - a.confidence);
  }

  // Update persona answers
  async updatePersonaAnswers(
    dealId: string, 
    personaType: string, 
    updates: Array<{
      chapterName: string;
      categoryName: string;
      questionIndex: number;
      answerIndex: number;
      answer: string;
      source_document: string;
      confidence: number;
    }>
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.apiCall('/ai-review/update-answers', {
        method: 'PUT',
        body: JSON.stringify({
          dealId,
          personaType,
          updates
        }),
      });
      return response;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update persona answers');
    }
  }

  // Helper method to get category from persona type
  private getCategoryFromPersonaType(type: string): string {
    const categoryMap: { [key: string]: string } = {
      'LEGAL': 'Legal',
      'FINANCIAL': 'Financial',
      'TECHNICAL': 'Technical',
      'OPERATIONAL': 'Operational',
      'COMPLIANCE': 'Compliance',
      'ESG': 'ESG',
      'STRATEGIC': 'Strategic',
      'RISK': 'Risk'
    };
    return categoryMap[type] || 'Other';
  }
}

export const aiReviewService = new AIReviewService();
